/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {AdminRuntimeService} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

import {ModelProbePanel} from './ModelProbePanel';
import {postAdminJson} from './postAdminJson';

const formatInvalidatedAt = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const SecretCachePanel = () => {
  const clearMutation = useMutation({
    mutationFn: () =>
      getService(AdminRuntimeService).clearSecretCacheApiV1AdminRuntimeSecretCachePost(),
  });

  return (
    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="16">
        <Stack gap="8">
          <Title size={18}>Secrets cache</Title>
          <Text color="subtle">Clear cached values after rotating a secret.</Text>
        </Stack>

        <Box>
          <Button
            variant="primary"
            onClick={() => clearMutation.mutate()}
            isLoading={clearMutation.isPending}
            disabled={clearMutation.isPending}
          >
            Clear secrets cache
          </Button>
        </Box>

        {clearMutation.data ? (
          <Box borderRadius="Medium" padding="12" backgroundColor="Background Subtle" role="status">
            <Text>
              Secrets cache cleared at {formatInvalidatedAt(clearMutation.data.invalidated_at)} by{' '}
              {clearMutation.data.requested_by}.
            </Text>
          </Box>
        ) : null}

        {clearMutation.error instanceof Error ? (
          <SystemBanner tone="alert">
            <Text>{clearMutation.error.message || 'Unable to clear the secrets cache.'}</Text>
          </SystemBanner>
        ) : null}
      </Stack>
    </Box>
  );
};

type CacheUsage = {
  ttft_ms: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  input_tokens: number;
};
type CacheProbeResult = CacheUsage & {cache_key: string};
type CacheBustResult = CacheUsage & {old_key: string; new_key: string};

const PromptCachePanel = () => {
  const probeMutation = useMutation({
    mutationFn: () => postAdminJson<CacheProbeResult>('/api/v1/ai/query/cache/probe'),
  });

  const bustMutation = useMutation({
    mutationFn: () => postAdminJson<CacheBustResult>('/api/v1/ai/query/cache/bust'),
  });

  const isLoading = probeMutation.isPending || bustMutation.isPending;
  const result = bustMutation.data ?? probeMutation.data;
  const error = bustMutation.error ?? probeMutation.error;

  return (
    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="16">
        <Stack gap="8">
          <Title size={18}>Prompt cache</Title>
          <Text color="subtle">
            Bust the OKAPI prompt cache (invalidates old key) and warm the new one. Probe measures
            TTFT to check if the cache is warm.
          </Text>
        </Stack>

        <Box display="flex" style={{gap: 8}}>
          <Button
            variant="primary"
            onClick={() => bustMutation.mutate()}
            isLoading={bustMutation.isPending}
            disabled={isLoading}
          >
            Bust + warm
          </Button>
          <Button
            variant="opacity"
            onClick={() => probeMutation.mutate()}
            isLoading={probeMutation.isPending}
            disabled={isLoading}
          >
            Probe
          </Button>
        </Box>

        {result ? (
          <Box borderRadius="Medium" padding="12" backgroundColor="Background Subtle" role="status">
            <Stack gap="4">
              <Box>
                {'new_key' in result ? (
                  <Text>
                    Cache busted: {(result as CacheBustResult).old_key} →{' '}
                    {(result as CacheBustResult).new_key}
                  </Text>
                ) : (
                  <Text>Cache key: {(result as CacheProbeResult).cache_key}</Text>
                )}
              </Box>
              <Box>
                <Text isBold>
                  TTFT: {result.ttft_ms}ms
                  {result.cache_read_tokens > 0 ? ' ✓ cache hit' : ' ✗ cache miss'}
                </Text>
              </Box>
              <Box>
                <Text size="small" color="subtle">
                  {result.cache_read_tokens.toLocaleString()} cached /{' '}
                  {result.input_tokens.toLocaleString()} total input tokens
                  {result.cache_write_tokens > 0 &&
                    ` (${result.cache_write_tokens.toLocaleString()} written to cache)`}
                </Text>
              </Box>
            </Stack>
          </Box>
        ) : null}

        {error instanceof Error ? (
          <SystemBanner tone="alert">
            <Text>{error.message || 'Request failed.'}</Text>
          </SystemBanner>
        ) : null}
      </Stack>
    </Box>
  );
};

export const AdminRuntime = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();

  useDocumentTitle('Runtime');

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Runtime', to: ROUTE_PATHS.ADMIN_RUNTIME},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>Runtime</Title>
        <SecretCachePanel />
        <PromptCachePanel />
        <ModelProbePanel />
      </Stack>
    </Layout.Container>
  );
};
